import { useMemo } from "react";
import { parseDecimalValue, formatDecimalPtBr } from "../utils/formatting";

const CAPACIDADE_TOLERANCIA_PERCENTUAL = 0.2;

const extractCapacidadeModelo = (option) => {
  if (!option) {
    return 0;
  }

  const base = option?.data || option?.dados || option;
  const candidates = [
    option?.CapacidadeModelo,
    option?.capacidadeModelo,
    base?.CapacidadeModelo,
    base?.capacidadeModelo,
    base?.ModeloVeicular?.CapacidadeModelo,
    base?.ModeloVeicular?.capacidadeModelo,
    base?.modeloVeicular?.CapacidadeModelo,
    base?.modeloVeicular?.capacidadeModelo,
    base?.Modelo?.CapacidadeModelo,
    base?.Modelo?.capacidadeModelo,
  ];

  const found = candidates.find(
    (candidate) => candidate !== undefined && candidate !== null && candidate !== ""
  );

  const parsed = parseDecimalValue(found);
  return parsed ?? 0;
};

const useCapacityFeedback = (selectedVeiculos, pesoDoPedidoRaw) => {
  const selectedVeiculosCount = selectedVeiculos?.length ?? 0;

  const capacidadeTotalSelecionada = useMemo(() => {
    return (selectedVeiculos || []).reduce((acc, option) => {
      const capacidade = extractCapacidadeModelo(option);
      if (Number.isFinite(capacidade)) {
        return acc + capacidade;
      }
      return acc;
    }, 0);
  }, [selectedVeiculos]);

  const pesoDoPedidoNumber = useMemo(() => {
    const parsed = parseDecimalValue(pesoDoPedidoRaw);
    if (parsed === null || Number.isNaN(parsed)) {
      return null;
    }
    return parsed;
  }, [pesoDoPedidoRaw]);

  const capacityFeedback = useMemo(() => {
    if (selectedVeiculosCount === 0 || !Number.isFinite(capacidadeTotalSelecionada)) {
      return null;
    }

    if (pesoDoPedidoNumber === null || pesoDoPedidoNumber <= 0) {
      return {
        type: "missing-weight",
        title: "Peso do pedido nao informado",
        description:
          "Informe o peso do pedido para comparar com a capacidade das frotas selecionadas.",
        background: "#FEF2F2",
        borderColor: "#FECACA",
        color: "#991B1B",
      };
    }

    const toleranceValue = pesoDoPedidoNumber * CAPACIDADE_TOLERANCIA_PERCENTUAL;
    const difference = capacidadeTotalSelecionada - pesoDoPedidoNumber;
    const differenceAbs = Math.abs(difference);
    const formattedDifference = formatDecimalPtBr(differenceAbs) ?? "0";

    if (difference > toleranceValue) {
      return {
        type: "above",
        title: "Capacidade muito acima",
        description: `As frotas nao atendem a solicitacao do pedido. A capacidade de carregamento esta muito acima (${formattedDifference} kg).`,
        background: "#FEF3C7",
        borderColor: "#FACC15",
        color: "#7C2D12",
      };
    }

    if (difference < -toleranceValue) {
      return {
        type: "below",
        title: "Capacidade muito abaixo",
        description: `As frotas nao atendem a solicitacao do pedido. A capacidade de carregamento esta muito abaixo (${formattedDifference} kg).`,
        background: "#FEF3C7",
        borderColor: "#FACC15",
        color: "#7C2D12",
      };
    }

    return {
      type: "ok",
      title: "Capacidade adequada",
      description: "As frotas selecionadas atendem a solicitacao do pedido.",
      background: "#ECFDF5",
      borderColor: "#A7F3D0",
      color: "#065F46",
    };
  }, [selectedVeiculosCount, capacidadeTotalSelecionada, pesoDoPedidoNumber]);

  return {
    selectedVeiculosCount,
    capacidadeTotalSelecionada,
    pesoDoPedidoNumber,
    capacityFeedback,
  };
};

export default useCapacityFeedback;
